/**
 * usersResource — the user-management list, authored against the resource-kit
 * v2 model/view contracts and consumed directly as a `ResourceBundle` by
 * `ResourcePage` (#1805 slice A3a; #1419 convergence of
 * the former 552-line bespoke `pages/Users.tsx`).
 *
 * The user list is a bounded set the backend caps at 100 rows/page
 * (`listUsersRequest` pages through), so the resource runs in `client`
 * pagination mode — search and pagination happen in memory over the fully
 * materialized set, as the old client-side table did.
 *
 * Still built IN-COMPONENT (`buildUsersResource(onEditRole)`, via a `useMemo` in
 * UsersPage) so the "Change Role" row action can be a `mode: 'handoff'` action
 * whose `onAction` is a closure that captured the page's `setEditingUser` —
 * clicking the menu item opens the page-owned RoleEditor with NO confirm, NO
 * toast, and NO busy entry (#1416 capability 4). Only the HOST is built per
 * call; the model and the two views are module-level, because none of them
 * depends on the closure.
 *
 * The factory's NAME AND SIGNATURE are part of the contract, not an
 * implementation detail: `services/__tests__/resource-searchable-parity.test.ts`
 * calls `buildUsersResource(() => {})` twice to hold this list's search box in
 * place (see `capabilities.search` below).
 *
 * Every cell projection lives in `./widgets.tsx` — all four columns carried
 * `fields[].render`, and that file explains why each reads the RECORD and why
 * each must declare `csvText`.
 */
import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
  ACTION_FEEDBACK_KIND,
  ACTION_MODE,
} from '@resource-kit/model';
import type { ResourceBundle, ResourceHost } from '@resource-kit/controllers/react';
import type { User } from '@/types/api';
import type { ListResult } from '@resource-kit/controllers';
import {
  listUsersRequest,
  resendInvitationRequest,
  revokeInvitationRequest,
} from '@/services/users';

/**
 * Present the identity list surface (`listUsersRequest` pages through the
 * backend cap and returns the full `User[]`) as the `ListResult` the shared
 * ResourcePage consumes. In `client` pagination mode ResourcePage fetches this
 * ONCE and runs search / pagination in memory, so `page` / `limit` here are
 * nominal (the client result drives the visible page).
 */
async function listAdapter(): Promise<ListResult<User>> {
  const users = await listUsersRequest();
  return { items: users, total: users.length, page: 1, limit: users.length || 1 };
}

const f = createFieldConstructors<User>();

/**
 * Each column carries a real accessor key, so the framework CSV export projects
 * the raw role / status even though the on-screen cell renders a badge (#1289
 * parity).
 *
 * All four declare `filterable: false`, and every one of them is load-bearing.
 * This list is `client`-paginated, which `useRemoteList` reports as source
 * `bulk`, and `listCapabilities.sourceDefault` returns TRUE for `filter` on any
 * non-remote source — in-memory filtering can never 400, so nothing upstream
 * holds the QueryBuilder back. There is no host `filterable` whitelist either,
 * and with none `deriveSchema` offers every filterable field. These four flags
 * are the ONLY reason `deriveQueryColumnsFromFields` returns `null` and the
 * filter bar stays hidden; drop one and the page grows a filter control it has
 * never had. `__tests__/usersResource.v2-parity.test.tsx` pins that.
 */
export const userModel = defineModel<User>()({
  name: 'user',
  idField: 'id',
  recName: 'email',
  fields: {
    email: f.text({
      label: 'users.columnEmail',
      filterable: false,
      widget: 'userEmailLink',
    }),
    name: f.text({
      label: 'users.columnName',
      filterable: false,
      widget: 'userName',
    }),
    role: f.text({
      label: 'users.columnRole',
      filterable: false,
      widget: 'userRoleBadge',
    }),
    status: f.text({
      label: 'users.columnStatus',
      filterable: false,
      widget: 'userStatus',
    }),
  },
});

export const userListView = defineListView(userModel, {
  items: [{ field: 'email' }, { field: 'name' }, { field: 'role' }, { field: 'status' }],
  /**
   * Declared TRUE, and unlike every other resource this one is not backed by a
   * route contract. The framework default has been hidden since #1642, but
   * this list is the rare case where the box is BOTH visible (no filterable
   * field ⇒ no QueryBuilder replaces it) and real: `client` pagination
   * searches the fetched rows in memory, so no backend `search` param is
   * involved and nothing can 400. Omitting the key lands on `useRemoteList`'s
   * `?? false` and deletes a WORKING search box.
   */
  capabilities: { search: true },
});

/** Read-only list: the detail surface is a page of its own; there is no form. */
export const userFormView = defineFormView(userModel, { groupDisplay: 'tab', children: [] });

/**
 * Build the users resource. `onEditRole` is the page closure the handoff
 * "Change Role" action hands control back to (opens the page-owned RoleEditor).
 */
export function buildUsersResource(
  onEditRole: (user: User) => void,
): ResourceBundle<User, typeof userModel.fields> {
  const host: ResourceHost<User> = {
    api: { list: listAdapter },
    label: 'users.title',
    slug: 'users',
    listTitle: 'users.title',
    listDescription: 'users.subtitle',
    pagination: 'client',
    // Restore the old page's 100-row initial page (the framework default is 10).
    defaultLimit: 100,
    // Row click leads to the canonical `/users/:id` detail page (parity with
    // the old email row link), rather than a drawer.
    detail: { surface: 'page' },
    rowActions: [
      {
        id: 'changeRole',
        label: 'users.changeRole',
        // Handoff (#1416 capability 4): open the page-owned RoleEditor
        // modal via the captured closure — no confirm, no toast, no busy.
        mode: ACTION_MODE.handoff,
        onAction: (row) => onEditRole(row),
      },
      {
        id: 'resendInvitation',
        label: 'users.resendInvitation',
        // Only an invitation still in flight can be resent (#1737). The
        // gate is what makes this safe to sit in a menu shared with every
        // row: on an account that already accepted, "resend invitation"
        // is not a no-op, it is a nonsense the backend answers with 409.
        isEligible: (row) => row.status === 'pending',
        onAction: (row) => resendInvitationRequest(row.id),
        feedback: () => ({
          kind: ACTION_FEEDBACK_KIND.success,
          message: 'users.invitationResent',
        }),
      },
      {
        id: 'revokeInvitation',
        label: 'users.revokeInvitation',
        isEligible: (row) => row.status === 'pending',
        // The one irreversible action on this page: it deletes the
        // pending account. `danger` + a confirm that names the
        // CONSEQUENCE (the address is freed, the link dies) rather than
        // asking a generic "are you sure" — the admin has to be able to
        // tell this apart from the resend sitting one line above it.
        intent: 'danger',
        confirm: {
          title: 'users.revokeInvitation',
          description: 'users.revokeInvitationConfirm',
        },
        onAction: (row) => revokeInvitationRequest(row.id),
        feedback: () => ({
          kind: ACTION_FEEDBACK_KIND.success,
          message: 'users.invitationRevoked',
        }),
      },
    ],
  };

  return {
    model: userModel,
    list: userListView,
    form: userFormView,
    host,
  };
}
